import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { recruiterRegister } from '../../api/recruiterAuth';
import AnimatedBackground from '../components/AnimatedBackground';
import { recruiterAuth } from '../recruiterAuth';
import styles from '../authPages.module.css';

export default function RecruiterSignup() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (recruiterAuth.isLoggedIn()) {
    return <Navigate to="/recruiter/search" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!name.trim() || !email.trim() || !password) {
        setError('Name, email, and password are required.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }

      const data = await recruiterRegister({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        company: company.trim() || undefined,
        password,
      });

      const token = data.accessToken || data.token;
      if (token) {
        recruiterAuth.setToken(token);
      }

      navigate('/recruiter/search', {
        replace: true,
        state: { signupSuccess: true },
      });
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className={styles.page}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <AnimatedBackground />

      <motion.div
        className={styles.card}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <motion.div className={styles.brandRow}>
          <span className={styles.logoMark} aria-hidden="true">
            S
          </span>
          <span className={styles.portalLabel}>Recruiter Portal</span>
        </motion.div>

        <h1 className={styles.title}>Create your account</h1>
        <p className={styles.subtitle}>Start with 10 free credits to browse verified candidates</p>

        {error ? (
          <div className={styles.error} role="alert">
            {error}
          </div>
        ) : null}

        <form onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="recruiter-name">
              Full name
            </label>
            <input
              id="recruiter-name"
              className={styles.input}
              type="text"
              autoComplete="name"
              placeholder="Jane Recruiter"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="recruiter-signup-email">
              Work email
            </label>
            <input
              id="recruiter-signup-email"
              className={styles.input}
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="recruiter-company">
              Company <span style={{ opacity: 0.5 }}>(optional)</span>
            </label>
            <input
              id="recruiter-company"
              className={styles.input}
              type="text"
              autoComplete="organization"
              placeholder="Acme Corp"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="recruiter-signup-password">
              Password
            </label>
            <input
              id="recruiter-signup-password"
              className={styles.input}
              type="password"
              autoComplete="new-password"
              placeholder="Min. 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="recruiter-confirm-password">
              Confirm password
            </label>
            <input
              id="recruiter-confirm-password"
              className={styles.input}
              type="password"
              autoComplete="new-password"
              placeholder="Repeat password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <motion.button
            type="submit"
            className={styles.submit}
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
          >
            {loading ? 'Creating account…' : 'Create account'}
          </motion.button>
        </form>

        <p className={styles.footer}>
          Already have an account? <Link to="/recruiter/login">Sign in</Link>
        </p>
      </motion.div>
    </motion.div>
  );
}
